import type { ComparisonValue } from '@epilot/journey-logic-commons/src/types/next'
import { LOGIC_OPERATOR } from '@epilot/journey-logic-commons/src/types/next'
import { parseISO } from 'date-fns'
import { z } from 'zod'

import { Operator } from '../operator'

const FactValueSchema = z.union([z.date(), z.string(), z.number().finite()])

const BoundSchema = z.union([
  z.string(), // e.g. '2024-06-01T12:00:00Z'
  z.number().finite()
])

const ComparisonValueSchema = z.object({
  min: BoundSchema,
  max: BoundSchema
})

export class Between extends Operator<Date | string | number> {
  constructor() {
    super(LOGIC_OPERATOR.BETWEEN, FactValueSchema)
  }

  evaluate(
    factValue: z.infer<typeof FactValueSchema>,
    comparisonValue: ComparisonValue
  ): boolean {
    const { min, max } = ComparisonValueSchema.parse(comparisonValue)

    if (
      factValue instanceof Date ||
      (typeof factValue === 'string' && isValidDate(factValue))
    ) {
      const elapsedMs = new Date(factValue).getTime()

      return (
        elapsedMs >= new Date(min).getTime() &&
        elapsedMs <= new Date(max).getTime()
      )
    }

    return Number(factValue) >= Number(min) && Number(factValue) <= Number(max)
  }
}

export const isValidDate = (value: string) => {
  /**
   * If the argument isn't a string, the function cannot parse the string or the values are invalid, it returns Invalid Date.
   * @see {@link https://date-fns.org/v4.1.0/docs/parseISO}
   */
  const parsed = parseISO(value)

  return Number.isNaN(parsed.getTime()) === false
}
